/**
 * Shared renderer for expanded column cells in the PNL grid.
 *
 * Iterates over active columns and renders each <td> using the column
 * definition's getValue + colorMode. Row-specific behavior (editable
 * forecast, clickable GL detail, department spread buttons) is handled
 * via the optional `renderOverride` callback.
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import type { ExpandedColumnDef, ColumnRenderContext, ColumnColorMode } from './columnRegistry';
import { ACTUAL_BASED_KEYS, monthHasActual, useGridDisplay } from './gridDisplayContext';
// @ts-ignore - JavaScript module without types
import { currencySymbol } from '../../../../utils/currency';

/**
 * Cell text for a comparison column. Variance columns read signed (+ / −),
 * amounts read as the collapsed cells do — with or without the currency
 * symbol depending on the shell — and a column that needs an actual the
 * month does not have yet reads "—" rather than a misleading $0.
 */
export function expandedCellText(
  col: ExpandedColumnDef,
  value: number,
  ctx: ColumnRenderContext,
  display: { closedMonthSet: Set<string>; hideSymbol: boolean }
): string {
  if (ACTUAL_BASED_KEYS.has(col.key) && !monthHasActual(display, ctx.monthIndex) && !(ctx.expanded?.actual)) return '\u2014';
  if (col.formatValue) return col.formatValue(value, ctx.currency);
  const strip = (text: string) => {
    if (!display.hideSymbol) return text;
    const symbol = String(currencySymbol(ctx.currency) || '');
    return symbol ? text.replace(symbol, '') : text;
  };
  if (col.colorMode === 'variance') {
    const body = strip(formatCurrency(Math.abs(value), false, ctx.currency));
    return `${value >= 0 ? '+' : '\u2212'}${body}`;
  }
  return strip(formatCurrency(value, true, ctx.currency));
}

interface ExpandedCellsRendererProps {
  columns: ExpandedColumnDef[];
  ctx: ColumnRenderContext;
  /** CSS classes for font weight, padding, text color */
  cellClassName: string;
  /** Optional per-column render override. Return a ReactNode to replace
   *  the default cell content, or undefined to use default rendering. */
  renderOverride?: (col: ExpandedColumnDef, value: number, ctx: ColumnRenderContext) => React.ReactNode | undefined;
  /** Override the color mode for specific columns (e.g. net income uses 'sign' for data cols) */
  colorModeOverride?: (col: ExpandedColumnDef) => ColumnColorMode;
}

function getColorClass(value: number, mode: ColumnColorMode): string {
  switch (mode) {
    case 'variance':
      return value >= 0 ? 'text-emerald-600' : 'text-red-600';
    case 'sign':
      return value >= 0 ? 'text-emerald-600' : 'text-red-600';
    case 'static':
    default:
      return '';
  }
}

export function ExpandedCellsRenderer({
  columns,
  ctx,
  cellClassName,
  renderOverride,
  colorModeOverride,
}: ExpandedCellsRendererProps) {
  const display = useGridDisplay();
  return (
    <>
      {columns.map((col) => {
        const value = col.getValue(ctx);
        const text = expandedCellText(col, value, ctx, display);
        const colorMode = colorModeOverride ? colorModeOverride(col) : col.colorMode;
        const colorClass = text === '\u2014' ? 'text-slate-400' : getColorClass(value, colorMode);

        // Check for row-specific override
        const override = renderOverride?.(col, value, ctx);
        if (override !== undefined) {
          return <React.Fragment key={col.key}>{override}</React.Fragment>;
        }

        // Default: read-only formatted cell
        return (
          <td
            key={col.key}
            className={`${col.isEditable ? '' : 'fv3-xcell '}text-right tabular-nums ${cellClassName} ${colorClass}`}
          >
            <span className="recalc-value">{text}</span>
          </td>
        );
      })}
    </>
  );
}
